import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';

export interface ModalData {
  title?: string;
  message?: string;
  icon?: string;
  buttons?: ModalButton[];
  width?: string;
  height?: string;
  customClass?: string;
}

export interface ModalButton {
  text: string;
  action: string;
  color?: 'primary' | 'accent' | 'warn';
  style?: 'basic' | 'filled' | 'outlined' | 'raised' | 'stroked';
}

@Component({
  selector: 'app-information-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule
  ],
  template: `
    <div class="modal-container" [ngClass]="data.customClass">
      <div class="modal-header" *ngIf="data.title || data.icon">
        <div class="header-left">
          <img 
            *ngIf="data.icon" 
            class="modal-icon"
            [src]="getIconPath(data.icon)"
            [alt]="data.icon">
          <h2 *ngIf="data.title" class="modal-title">{{ data.title }}</h2>
        </div>
        <button mat-icon-button class="close-button" (click)="closeModal()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      
      <div class="modal-content" *ngIf="data.message">
        <p [innerHTML]="data.message"></p>
      </div>
      
      <div class="modal-actions" *ngIf="data.buttons && data.buttons.length > 0">
        <ng-container *ngFor="let button of data.buttons">
          <button 
            *ngIf="!button.style || button.style === 'basic'"
            matButton
            [color]="button.color || 'primary'"
            (click)="onButtonClick(button.action)">
            {{ button.text }}
          </button>
          <button 
            *ngIf="button.style === 'raised' || button.style === 'filled'"
            matButton="filled"
            [color]="button.color || 'primary'"
            (click)="onButtonClick(button.action)">
            {{ button.text }}
          </button>
          <button 
            *ngIf="button.style === 'stroked' || button.style === 'outlined'"
            matButton="outlined"
            [color]="button.color || 'primary'"
            (click)="onButtonClick(button.action)">
            {{ button.text }}
          </button>
        </ng-container>
      </div>
    </div>
  `,
  styles: [`
    .modal-container {
      padding: 1.5rem;
      min-width: 280px;
      max-width: 560px;
      border-radius: 1rem;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 1.5rem;
      border-bottom: 1px solid #e0e0e0;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .close-button {
      color: #606A73;
    }

    .close-button:hover {
      color: #293744;
      background-color: #f0f0f0;
    }

    .modal-icon {
      width: 1.5rem;
      height: 1.5rem;
    }

    .modal-title {
      font-weight: 700;
      font-size: 1.25rem;
      line-height: 1.875rem;
      color: #293744;
    }

    .modal-content {
      padding-bottom: 1.5rem;
      padding-top: 1.5rem;
      color: #666;
      line-height: 1.5;
      border-bottom: 1px solid #e0e0e0;
    }

    .modal-content p {
     font-weight: 400;
     font-size: 1rem;
     line-height: 1.5rem;
     color: #606A73;
    }

    .modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      padding-top: 1.5rem;
    }

    .modal-actions button {
      min-width: 80px;
    }

    @media (max-width: 600px) {
      .modal-container {
        padding: 16px;
        min-width: 250px;
      }

      .modal-actions {
        flex-direction: column-reverse;
        gap: 8px;
      }

      .modal-actions button {
        width: 100%;
      }
    }
  `]
})
export class InformationModalComponent {
  constructor(
    public dialogRef: MatDialogRef<InformationModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ModalData
  ) { }

  onButtonClick(action: string): void {
    this.dialogRef.close(action);
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  getIconPath(iconName: string): string {
    return `assets/svg/icons/${iconName}.svg`;
  }
}